import { useTranslation } from "react-i18next";
import { ScrollArea, ScrollBar } from "@primitives/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@primitives/collapsible";
import { ChevronDown } from "lucide-react";
import { ChatEntity } from "@/indexedDB/types";
import ChatListEntry from "./ChatListEntry";

/**
 * Purpose: Displays archived chats in a collapsible, scrollable section
 *
 * How it works:
 * 1. Takes an array of archived chats as input
 * 2. Renders a collapsible section with a trigger showing the count
 * 3. When expanded, displays chats in a scrollable area
 *
 * Key features:
 * - Consistent styling across different chat list views
 * - Proper scrolling behavior for many archived chats
 * - Collapsible UI to save space when not in use
 */
interface ArchivedChatsSectionProps {
  archivedChats: ChatEntity[];
}

export default function ArchivedChatsSection({
  archivedChats,
}: ArchivedChatsSectionProps) {
  const { t } = useTranslation();

  if (archivedChats.length === 0) {
    return null;
  }

  return (
    <Collapsible className="m-1 mt-0 max-h-[40vh] rounded-md border border-border bg-card">
      <CollapsibleTrigger className="flex w-full items-center justify-between p-2 hover:bg-accent/10">
        <span className="text-sm font-medium">
          {t("archivedChats")} ({archivedChats.length})
        </span>
        <ChevronDown className="size-4" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ScrollArea className="h-[30vh] p-1 pb-3">
          <div className="space-y-1">
            {archivedChats.map((chat) => (
              <ChatListEntry key={chat.id} chat={chat} />
            ))}
          </div>
          <ScrollBar orientation="vertical" />
        </ScrollArea>
      </CollapsibleContent>
    </Collapsible>
  );
}
